import React, { useCallback, useEffect, useState } from "react";
import {
  Breadcrumbs,
  Button,
  DetailDrawer,
  InnoTable,
  Popover,
  TableList,
  Tabs,
} from "InnoUI/components";
import SC from "./style";
import { useLocation, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { apiGet } from "api/callApi";
import TooltipText from "components/TooltipText";
import theme, { getBorderColor } from "@theme";
import mockData from "mockData/LeasingLayout";
import { formatDateTime, innoTableConfig } from "utils/config";
import { requestType, requestTypeForUpdate } from "utils/optionData/utils";
// import JournalDetails from "./JournalDetails";
import debounce from "lodash.debounce";
import JournalDetails from "views/ProjectHome/Journal/JournalDetails";
import TimezoneConverter from "components/TimeZoneConvertor";
import { formatAlarmDateTime } from "utils/helper";
import CustomAccordion from "InnoUI/components/CustomAccordion";
import PointIdIndicatorInJournal from "components/PointIdIndicatorInJournal";

const ActiveJournals = (props) => {
  const { popupState } = props;
  const [data, setData] = useState(mockData);
  const [record, setTotalRecord] = useState();
  const [selectedTab, setSelectedTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const occupantId = searchParams.get("projectId");
  const folderId = searchParams.get("folderId");
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    requestType: null,
    searchValue: null,
    propertyIds: null,
    roles: null,
  });

  const currentProject = useSelector(
    (state) => state?.currentProject?.data || [],
  );
  const roleOptionData = useSelector(
    (state) => state?.roleOptionData?.data || [],
  );

  const occupantOption = useSelector(
    (state) => state?.occupantOption?.data || [],
  );

  const breadcrumbItems = [
    {
      label: `${currentProject.propertyName || ""}`,
      href: `/project-home`,
    },
    { label: "Folder/Files" },
  ];

  const handleBreadcrumbClick = (href) => {
    if (href) {
      navigate(href);
    }
  };

  const projectNameFilter = {
    filters: [
      {
        text: currentProject?.propertyName || "Current Project",
        value: currentProject?.id || "current_project_id",
      },
      ...(Array.isArray(occupantOption)
        ? occupantOption.map((option) => ({
            text: option?.occupantName,
            value: option?.id,
          }))
        : []),
    ],
    onFilter: (value, record) => {
      return true;
    },
    filterSearch: true,
    filterMode: "tree",
  };

  const requestFilter = {
    filters: requestTypeForUpdate.map((option) => ({
      text: option.label,
      value: option.id,
    })),
    onFilter: (value, record) => {
      return true;
    },
    filterSearch: true,
    filterMode: "tree",
  };

  const roleFilter = {
    filters: roleOptionData.map((option) => ({
      text: option.role,
      value: option.id,
    })),
    onFilter: (value, record) => {
      return true;
    },
    filterSearch: true,
    filterMode: "tree",
  };

  const getJournalData = async () => {
    try {
      setIsLoading(true);
      const { requestType, searchValue, propertyIds, roles } = filters;
      let url = `/v1/journal?projectId=${occupantId}&type=${type}&pageSize=${pageSize}&pageNo=${currentPage}`;

      if (requestType) {
        url = `${url}&requestType=${requestType}`;
      }
      if (searchValue && searchValue.trim() !== "") {
        url += `&searchValue=${searchValue.trim()}`;
      }
      if (propertyIds) {
        url += `&propertyIds=${propertyIds}`;
      }
      if (roles) {
        url += `&roles=${roles}`;
      }
      if (selectedTab == 0 || selectedTab == 1) {
        url = `${url}&status=${selectedTab ? 0 : 1}`;
      }
      const response = await apiGet(url, false);
      setIsLoading(false);
      setTotalRecord(response);
      const journalData = Array.isArray(response.data) ? response.data : [];
      setData(journalData);
    } catch (error) {
      console.log("Error", error);
    }
  };

  useEffect(() => {
    getJournalData();
  }, [pageSize, currentPage, filters, occupantId, selectedTab]);

  const handlePagination = (page, pageSize) => {
    setPageSize(pageSize);
    setCurrentPage(page);
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      setFilters((prevFilters) => ({ ...prevFilters, searchValue: query }));
      setCurrentPage(1);
    }, 500),
    [],
  );

  const handleSearch = useCallback(
    (event) => {
      const query = event.target.value.trim();

      // Update searchValue in filters with debounce
      if (query === "") {
        // Reset the search filter and fetch data
        setFilters((prevFilters) => ({ ...prevFilters, searchValue: null }));
        setCurrentPage(1);
      } else {
        // Only debounce valid search queries
        debouncedSearch(query);
      }
    },
    [debouncedSearch],
  );

  const onRowClick = useCallback(() => {}, []);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const tabData = [
    { value: 0, label: `Active Journal` },
    { value: 1, label: "Closed Journal" },
  ];

  const onAddDataCallback = () => {
    navigate(`/journal/add-journal?projectId=${occupantId}&type=${type}`);
  };

  const handleUpdate = (record) => {
    navigate(
      `/journal/update-journal?projectId=${occupantId}&type=${type}&journalId=${record.id}`,
    );
  };

  const handleJournalClick = (record) => {
    setSelectedRow(record.id);
    setOpen(true);
  };

  const closeDrawer = () => {
    setOpen(false);
  };

  const handleThread = (record) => {
    navigate(
      `/journal/open-thread?projectId=${occupantId}&folderId=${folderId}&type=${type}&journalId=${record.id}`,
    );
  };

  const journalColumn = [
    {
      title: "Ingested",
      width: 50,
      dataIndex: "pointId",
      render(value, record) {
        return record.pointId ? <PointIdIndicatorInJournal /> : "--";
      },
    },

    {
      title: "Journal Subject",
      width: 400,
      dataIndex: "subject",
      render(value, record) {
        const menuItems = [
          {
            id: 1,
            text: "Open Thread",
            onClick: (e) => {
              e.stopPropagation();
              handleThread(record);
            },
          },
          // {
          //   id: 2,
          //   text: "Update",
          //   onClick: (e) => {
          //     e.stopPropagation();
          //     handleUpdate(record);
          //   },
          // },
        ];
        return (
          <SC.PopoverContainer>
            {/* <TooltipText title={value} maxLength={60} /> */}
            {value}
            <Popover
              sx={{
                cursor: "pointer",
              }}
              buttonLabel={{
                icon: (
                  <SC.PopoverSpan>
                    <SC.MoreVertIcon />
                  </SC.PopoverSpan>
                ),
              }}
              popoverContent={
                <TableList
                  sx={{
                    borderBottom: `1px solid rgba(227, 110, 0, 0.1)`,
                  }}
                  items={menuItems || []}
                  popupState={popupState}
                />
              }
            />
          </SC.PopoverContainer>
        );
      },
    },
    {
      title: "Request Type",
      dataIndex: "requestType",
      render(value, record) {
        const request = requestTypeForUpdate.find((item) => item.id === value);
        return (
          <p
            style={{
              color: theme.colors.modernGray[700],
              fontWeight: 500,
              margin: 0,
            }}
          >
            {request ? request.label : "Unknown"}
          </p>
        );
      },
      ...requestFilter,
    },
    {
      title: "Request To",
      width: 150,
      dataIndex: "roles",
      render(value, record) {
        return (
          <Popover
            sx={{
              cursor: "pointer",
            }}
            buttonLabel={{
              icon: (
                <p
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "150px",
                    color: theme.colors.modernGray[600],
                    fontWeight: 500,
                    margin: 0,
                  }}
                >
                  {value}
                </p>
              ),
            }}
            popoverContent={
              <table style={{ width: "200px" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        background:
                          "linear-gradient(135deg, #E36E00 0%, #FFAE00 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        borderBottom: `1px solid rgba(227, 110, 0, 0.2)`,
                        textAlign: "left",
                        fontWeight: 600,
                        padding: "12px",
                      }}
                    >
                      Roles
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ marginBottom: "20px" }}>
                    <td
                      style={{
                        borderBottom: `1px solid rgba(227, 110, 0, 0.1)`,
                        padding: "12px",
                        color: theme.colors.modernGray[600],
                      }}
                    >
                      {value || "N/A"}
                    </td>
                  </tr>
                </tbody>
              </table>
            }
          />
        );
      },
      ...roleFilter,
    },
    {
      title: "To",
      width: 250,
      dataIndex: "sendTo",
      render(value, record) {
        return (
          <Popover
            sx={{
              cursor: "pointer",
            }}
            buttonLabel={{
              icon: (
                <p
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "150px",
                    color: theme.colors.modernGray[600],
                    fontWeight: 500,
                    margin: 0,
                  }}
                >
                  {value}
                </p>
              ),
            }}
            popoverContent={
              <table style={{ width: "200px" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        background:
                          "linear-gradient(135deg, #E36E00 0%, #FFAE00 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        borderBottom: `1px solid rgba(227, 110, 0, 0.2)`,
                        textAlign: "left",
                        fontWeight: 600,
                        padding: "12px",
                      }}
                    >
                      Email
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ marginBottom: "20px" }}>
                    <td
                      style={{
                        borderBottom: `1px solid rgba(227, 110, 0, 0.1)`,
                        padding: "12px",
                        color: theme.colors.modernGray[600],
                      }}
                    >
                      {value || "N/A"}
                    </td>
                  </tr>
                </tbody>
              </table>
            }
          />
        );
      },
    },
    {
      title: "Created On",
      dataIndex: "createdOn",
      width: 150,
      render(value, record) {
        return (
          <span
            style={{
              color: theme.colors.modernGray[600],
              fontWeight: 500,
            }}
          >
            <TimezoneConverter time={value} />
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render(value, record) {
        return (
          <span
            style={{
              color:
                value === 1
                  ? theme.colors.modernSuccess
                  : theme.colors.modernError,
              fontWeight: 600,
              padding: "4px 12px",
              background:
                value === 1
                  ? "rgba(16, 185, 129, 0.1)"
                  : "rgba(239, 68, 68, 0.1)",
              borderRadius: theme.modernTokens.borderRadius.full,
              border:
                value === 1
                  ? "1px solid rgba(16, 185, 129, 0.2)"
                  : "1px solid rgba(239, 68, 68, 0.2)",
              fontSize: "12px",
            }}
          >
            {value === 1 ? "Active" : "Closed"}
          </span>
        );
      },
    },
    {
      title: "Alarm set for",
      width: 150,
      render(value, record) {
        const alarmData = record?.alarmData || [];
        const primaryAlarms = alarmData.filter(
          (alarm) => alarm.alarmType === 0,
        );
        if (!primaryAlarms.length) return <p>-</p>;
        const { alarmDate, alarmTime } = primaryAlarms[0];
        return <p>{formatAlarmDateTime(alarmDate, alarmTime)}</p>;
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      render(value, record) {
        return (
          <Button
            caption={"View"}
            onClick={() => handleJournalClick(record)}
            color="white"
            fontSize="12px"
            style={{
              background: "linear-gradient(135deg, #E36E00 0%, #FFAE00 100%)",
              color: theme.colors.white,
              minWidth: "60px",
              height: "32px",
              borderRadius: theme.modernTokens.borderRadius.md,
              textTransform: "normal",
              fontWeight: 600,
              border: "none",
              boxShadow: theme.modernTokens.shadows.sm,
              transition: theme.modernTokens.transitions.normal,
            }}
          />
        );
      },
    },
  ];

  if (type == 0) {
    journalColumn.splice(4, 0, {
      title: "Property Name",
      width: 150,
      dataIndex: "propertyName",
      render(value, record) {
        return <p>{value}</p>;
      },
      ...projectNameFilter,
    });
  }

  const onFilterChange = (data) => {
    const { requestType, propertyName, roles } = data;
    setFilters({
      ...filters,
      requestType: requestType,
      propertyIds: propertyName,
      roles: roles,
    });
  };

  const tableConfig = {
    draggable: false,
    selection: false,
    expandable: false,
    pagination: true,
    addNew: false,
    search: false,
    action: false,
    divBorder: false,
  };

  const subColumnData = [
    {
      title: "Journal Subject",
      width: 350,
      dataIndex: "subject",
      render(value, record) {
        const menuItems = [
          {
            id: 1,
            text: "Open Thread",
            onClick: (e) => {
              e.stopPropagation();
              handleThread(record);
            },
          },
          {
            id: 2,
            text: "Update",
            onClick: (e) => {
              e.stopPropagation();
              handleUpdate(record);
            },
          },
        ];
        return (
          <SC.PopoverContainer>
            <TooltipText title={value} maxLength={60} />
            <Popover
              sx={{
                cursor: "pointer",
              }}
              buttonLabel={{
                icon: (
                  <SC.PopoverSpan>
                    <SC.MoreVertIcon />
                  </SC.PopoverSpan>
                ),
              }}
              popoverContent={
                <TableList
                  sx={{
                    borderBottom: `1px solid rgba(227, 110, 0, 0.1)`,
                  }}
                  items={menuItems || []}
                  popupState={popupState}
                />
              }
            />
          </SC.PopoverContainer>
        );
      },
    },
    {
      title: "Request Type",
      dataIndex: "requestType",
      render(value, record) {
        const request = requestTypeForUpdate.find((item) => item.id === value);
        return (
          <p
            style={{
              color: theme.colors.modernGray[700],
              fontWeight: 500,
              margin: 0,
            }}
          >
            {request ? request.label : "Unknown"}
          </p>
        );
      },
      ...requestFilter,
    },
    {
      title: "Request To",
      width: 150,
      dataIndex: "roles",
      render(value, record) {
        return (
          <Popover
            sx={{
              cursor: "pointer",
            }}
            buttonLabel={{
              icon: (
                <p
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "150px",
                    color: theme.colors.modernGray[600],
                    fontWeight: 500,
                    margin: 0,
                  }}
                >
                  {value}
                </p>
              ),
            }}
            popoverContent={
              <table style={{ width: "200px" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        background:
                          "linear-gradient(135deg, #E36E00 0%, #FFAE00 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        borderBottom: `1px solid rgba(227, 110, 0, 0.2)`,
                        textAlign: "left",
                        fontWeight: 600,
                        padding: "12px",
                      }}
                    >
                      Roles
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ marginBottom: "20px" }}>
                    <td
                      style={{
                        borderBottom: `1px solid rgba(227, 110, 0, 0.1)`,
                        padding: "12px",
                        color: theme.colors.modernGray[600],
                      }}
                    >
                      {value || "N/A"}
                    </td>
                  </tr>
                </tbody>
              </table>
            }
          />
        );
      },
      ...roleFilter,
    },
    {
      title: "To",
      width: 250,
      dataIndex: "sendTo",
      render(value, record) {
        return (
          <Popover
            sx={{
              cursor: "pointer",
            }}
            buttonLabel={{
              icon: (
                <p
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "150px",
                    color: theme.colors.modernGray[600],
                    fontWeight: 500,
                    margin: 0,
                  }}
                >
                  {value}
                </p>
              ),
            }}
            popoverContent={
              <table style={{ width: "200px" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        background:
                          "linear-gradient(135deg, #E36E00 0%, #FFAE00 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        borderBottom: `1px solid rgba(227, 110, 0, 0.2)`,
                        textAlign: "left",
                        fontWeight: 600,
                        padding: "12px",
                      }}
                    >
                      Email
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ marginBottom: "20px" }}>
                    <td
                      style={{
                        borderBottom: `1px solid rgba(227, 110, 0, 0.1)`,
                        padding: "12px",
                        color: theme.colors.modernGray[600],
                      }}
                    >
                      {value || "N/A"}
                    </td>
                  </tr>
                </tbody>
              </table>
            }
          />
        );
      },
    },
    {
      title: "Created On",
      dataIndex: "createdOn",
      render(value, record) {
        return (
          <span
            style={{
              color: theme.colors.modernGray[600],
              fontWeight: 500,
            }}
          >
            <TimezoneConverter time={value} />
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render(value, record) {
        return (
          <span
            style={{
              color:
                value === 1
                  ? theme.colors.modernSuccess
                  : theme.colors.modernError,
              fontWeight: 600,
              padding: "4px 12px",
              background:
                value === 1
                  ? "rgba(16, 185, 129, 0.1)"
                  : "rgba(239, 68, 68, 0.1)",
              borderRadius: theme.modernTokens.borderRadius.full,
              border:
                value === 1
                  ? "1px solid rgba(16, 185, 129, 0.2)"
                  : "1px solid rgba(239, 68, 68, 0.2)",
              fontSize: "12px",
            }}
          >
            {value === 1 ? "Active" : "Closed"}
          </span>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      render(value, record) {
        return (
          <Button
            caption={"View"}
            onClick={() => handleJournalClick(record)}
            color="white"
            fontSize="12px"
            style={{
              background: "linear-gradient(135deg, #E36E00 0%, #FFAE00 100%)",
              color: theme.colors.white,
              minWidth: "60px",
              height: "32px",
              borderRadius: theme.modernTokens.borderRadius.md,
              textTransform: "normal",
              fontWeight: 600,
              border: "none",
              boxShadow: theme.modernTokens.shadows.sm,
              transition: theme.modernTokens.transitions.normal,
            }}
          />
        );
      },
    },
  ];

  return (
    <div style={{ marginBottom: "24px" }}>
      <SC.HeaderContainer>
        <SC.HeaderStyle>Active Journals</SC.HeaderStyle>
        <SC.CountStyle>{data?.length || 0}</SC.CountStyle>
      </SC.HeaderContainer>
      <SC.CardContainerStyle>
        <SC.TabDiv>
          <Tabs
            value={selectedTab}
            label={tabData.map((tab) => tab.label)}
            onChange={handleTabChange}
            selectedValue={selectedTab}
          />
          <Button
            caption="New Journal"
            onClick={onAddDataCallback}
            fontSize="12px"
            color="white"
            style={{
              marginBottom: 8,
              background: theme.gradient.themeGradient,
              color: theme.colors.white,
              minWidth: "150px",
              height: "30px",
              borderRadius: "3px",
            }}
          />
        </SC.TabDiv>
        <InnoTable
          tableConfig={tableConfig}
          loading={isLoading}
          padding="16px"
          column={journalColumn}
          data={data}
          tableKey="Journal"
          searchKeys={["subject", "roles", "sendTo"]}
          selectedItems={[]}
          onAddDataCallback={onAddDataCallback}
          onFilterChange={onFilterChange}
          onRowClick={onRowClick}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: record?.total || 0,
            onChange: handlePagination,
          }}
        />
      </SC.CardContainerStyle>

      {open && (
        <DetailDrawer
          open={open}
          onClose={closeDrawer}
          title={false}
          closable={true}
          width={"865px"}
          children={
            <JournalDetails
              onClose={closeDrawer}
              selectedRowId={selectedRow}
              fetchData={getJournalData}
            />
          }
        ></DetailDrawer>
      )}
    </div>
  );
};

export default ActiveJournals;
